import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { familyService } from '@services/familyService';
import toast from 'react-hot-toast';

const QUERY_KEY = 'family';

export const useFamily = () => {
  const queryClient = useQueryClient();

  const { data: members = [], isLoading: isLoadingMembers } = useQuery({
    queryKey: [QUERY_KEY, 'members'],
    queryFn: familyService.getMembers,
  });

  const { data: invites = [], isLoading: isLoadingInvites } = useQuery({
    queryKey: [QUERY_KEY, 'invites'],
    queryFn: familyService.getInvites,
  });

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'members'] });
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY, 'invites'] });
  };

  const inviteMemberMutation = useMutation({
    mutationFn: familyService.inviteMember,
    onSuccess: () => {
      invalidateAll();
      toast.success('Convite enviado com sucesso!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao enviar convite'),
  });

  const cancelInviteMutation = useMutation({
    mutationFn: familyService.cancelInvite,
    onSuccess: () => {
      invalidateAll();
      toast.success('Convite cancelado!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao cancelar convite'),
  });

  const resendInviteMutation = useMutation({
    mutationFn: familyService.resendInvite,
    onSuccess: () => {
      invalidateAll();
      toast.success('Convite reenviado!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao reenviar convite'),
  });

  const removeMemberMutation = useMutation({
    mutationFn: familyService.removeMember,
    onSuccess: () => {
      invalidateAll();
      toast.success('Membro removido!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao remover membro'),
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: ({ userId, role }) => familyService.updateMemberRole(userId, role),
    onSuccess: () => {
      invalidateAll();
      toast.success('Função atualizada!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao atualizar função'),
  });

  const transferOwnershipMutation = useMutation({
    mutationFn: familyService.transferOwnership,
    onSuccess: () => {
      invalidateAll();
      toast.success('Propriedade transferida com sucesso!');
    },
    onError: (error) => toast.error(error.response?.data?.error?.message || 'Erro ao transferir propriedade'),
  });

  return {
    members,
    invites,
    isLoadingMembers,
    isLoadingInvites,
    inviteMember: inviteMemberMutation.mutateAsync,
    cancelInvite: cancelInviteMutation.mutateAsync,
    resendInvite: resendInviteMutation.mutateAsync,
    removeMember: removeMemberMutation.mutateAsync,
    updateMemberRole: updateMemberRoleMutation.mutateAsync,
    transferOwnership: transferOwnershipMutation.mutateAsync,
    isInviting: inviteMemberMutation.isPending,
    isCanceling: cancelInviteMutation.isPending,
    isResending: resendInviteMutation.isPending,
    isRemoving: removeMemberMutation.isPending,
    isUpdatingRole: updateMemberRoleMutation.isPending,
    isTransferring: transferOwnershipMutation.isPending,
  };
};

export default useFamily;
